import { z } from 'zod';
import { evaluateExpression, validateCSVFormat, parseCSVLine } from '../shared';

export const baseSchema = z.object({
  project: z.string().min(1, 'Project is required'),
  title: z.string().min(1, 'Title is required'),

  L: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Length must be a valid expression greater than 0'),

  W: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n > 0;
  }, 'Tributary width must be a valid expression greater than 0'),

  addedWeight: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Added Weight must be a valid expression greater than or equal to 0'),

  support: z.coerce
    .number()
    .int()
    .refine(n => [1, 2, 3, 4, 5, 6].includes(n), 'Support type must be between 1 and 6'),

  sectionName: z.string('Section name is required'),

  numStuds: z.coerce.number().int().min(1, 'Number of studs must be at least 1'),

  restraintLength: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Restraint length must be a valid expression greater than or equal to 0'),

  axialLoad: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'Axial load must be a valid expression greater than or equal to 0'),

  pressureHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  timeHistory: z.string().superRefine((val, ctx) => {
    const err = validateCSVFormat(val);
    if (err) ctx.addIssue({ code: 'custom', message: err });
  }),

  u0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'u0 must be a valid expression'),

  v0: z.string().refine(s => {
    const n = evaluateExpression(s);
    return n !== null && n >= 0;
  }, 'v0 must be a valid expression')
});

/**
 * Full schema with cross-field validation
 */
export const schema = baseSchema.superRefine((data, ctx) => {
  if (parseCSVLine(data.pressureHistory)?.length !== parseCSVLine(data.timeHistory)?.length) {
    const msg = 'Pressure and time histories must have the same number of values.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['pressureHistory'] });
    ctx.addIssue({ code: 'custom', message: msg, path: ['timeHistory'] });
  }

  if (evaluateExpression(data.restraintLength)! > evaluateExpression(data.L)!) {
    const msg = 'Restraint length cannot be greater than the length of the element.';
    ctx.addIssue({ code: 'custom', message: msg, path: ['restraintLength'] });
  }
});

/**
 * Helper types
 */
export type FieldName = keyof z.infer<typeof baseSchema>;
export type FormValues = z.input<typeof schema>; // raw form shape (strings from inputs/selects)
export type ParsedValues = z.output<typeof schema>; // parsed shape (numbers after coercion)
export type FormErrors = Partial<Record<FieldName, string | null>>;

/**
 * Validate a single field
 */
export function validateOneField<K extends FieldName>(key: K, raw: unknown): string | null {
  const fieldSchema = baseSchema.shape[key];
  const res = fieldSchema.safeParse(raw);
  return res.success ? null : (res.error.issues[0]?.message ?? 'Invalid value');
}
